import Alpine from "alpinejs";

/**
 * I clone and render the given source template.
 * Source: https://www.bennadel.com/blog/4602-creating-a-template-outlet-directive-in-alpine-js-3-13-5.htm
 */
function TemplateOutletDirective(element, metadata, framework) {
    // Get the template reference that we want to clone and render.
    var templateRef = framework.evaluate(metadata.expression);
    // Clone the template and get the root node - this is the node that we will
    // inject into the DOM.
    var clone = templateRef.content
        .cloneNode(true)
        .firstElementChild
    ;
    // CAUTION: The following logic ASSUMES that the template-outlet directive has
    // an "x-data" scope binding on it. If it didn't we would have to change the
    // logic. But, I don't think Alpine.js has mechanics to solve this use-case
    // quite yet.
    Alpine.addScopeToNode(
        clone,
        // Use the "x-data" scope from the template-outlet element as a means to
        // supply initializing data to the clone (for constructor injection).
        Alpine.closestDataStack(element)[0],
        // use the template-outlet element's parent to define the rest of the
        // scope chain.
        element.parentElement
    );
    // Instead of leaving the template in the DOM, we're going to swap the
    // template with a comment hook. This isn't necessary; but, I think it leaves
    // the DOM more pleasant looking.
    var domHook = document.createComment(` Template outlet hook (${metadata.expression}) with bindings (${element.getAttribute("x-data")}). `);
    domHook._template_outlet_ref = templateRef;
    domHook._template_outlet_clone = clone;
// Swap the template-outlet element with the hook and clone.
// --
// NOTE: Doing this inside the mutateDom() method will pause Alpine's internal
// MutationObserver, which allows us to perform DOM manipulation without
// triggering actions in the framework. Then, we can call initTree() and
// destroyTree() to have explicitly setup and teardowm DOM node bindings.
    Alpine.mutateDom(
        function pauseMutationObserver() {
            element.after(domHook);
            domHook.after(clone);
            Alpine.initTree(clone);

            element.remove();
            Alpine.destroyTree(element);

        }
    );
}

document.addEventListener('alpine:init', () => {
    Alpine.directive("template-outlet", TemplateOutletDirective);
});

window.onLogoutSubmit = async () => {
    await fetch('/umbraco/api/login/logout', {
        method: "Post"
    });
    location.reload();
}

window.onLoginSubmit = async (ev: SubmitEvent, data: any) => {
    try {
        data.hasError = false;
        data.isSubmitting = true;
        const formData = new FormData(ev.target as HTMLFormElement);
        const formDataObject = Object.fromEntries(formData.entries());
        const result = await fetch('/umbraco/api/login/login', {
            method: "Post",
            body: JSON.stringify(formDataObject),
            headers: {
                'Content-Type': 'application/json'
            }
        });
        if (result.ok) {
            location.href = `/${await result.text()}`;
        } else {
            data.hasError = true;
        }
    } catch {
        data.hasError = true;
    } finally {
        data.isSubmitting = false;
    }
}

window.onSubmitChangePassword = async (ev: SubmitEvent, data: any) => {
    try {
        data.errors = [];
        data.hasError = false;
        data.isSubmitting = true;
        const formData = new FormData(ev.target as HTMLFormElement);
        const formDataObject = Object.fromEntries(formData.entries());
        const result = await fetch('/umbraco/api/user/changepassword', {
            method: "Post",
            body: JSON.stringify(formDataObject),
            headers: {
                'Content-Type': 'application/json'
            }
        });
        if (result.ok) {
            data.showChangePassword = false;
        } else {
            data.hasError = true;
            data.errors = await result.json();
        }
    } catch {
        data.hasError = true;
    } finally {
        data.isSubmitting = false;
    }
}

window.onForgottenPasswordSubmit = async (ev: SubmitEvent, data: any) => {
    try {
        data.hasError = false;
        data.isSubmitting = true;
        const formData = new FormData(ev.target as HTMLFormElement);
        const formDataObject = Object.fromEntries(formData.entries());
        const result = await fetch('/umbraco/api/login/forgottenpassword', {
            method: "Post",
            body: JSON.stringify(formDataObject),
            headers: {
                'Content-Type': 'application/json'
            }
        });
        if (result.ok) {
            data.isSuccess = true;
        } else {
            data.hasError = true;
        }
    } catch {
        data.hasError = true;
    } finally {
        data.isSubmitting = false;
    }
}

window.onProjectListSearch = async (ev: Event, data: any) => {
    const formDataObject: any = mapFormToObject(ev);
    if (ev.type == "input") {
        formDataObject.pageNumber = 0;
    }
    data.filtersCount = getFiltersCount(formDataObject.filters);
    const res = await fetch("/umbraco/api/search/projects", {
        method: "POST",
        body: JSON.stringify(formDataObject),
        headers: {
            "Content-Type": "application/json",
            "domain-id": document.querySelector("html")!.getAttribute("domain-id")
        }
    });
    const projects = await res.json();
    data.projects = projects.projects;
    data.facets = projects.facets
    data.pagination = projects.pagination;
    data.currentPage = projects.pagination.currentPage;
}

window.onDownloadsSearch = async (ev: Event, data: any) => {
    ev.preventDefault();
    const formDataObject: any = mapFormToObject(ev);
    if (ev.type == "input") {
        formDataObject.pageNumber = 0;
    }
    formDataObject.pageSize = data.pageSize;
    const res = await fetch("/umbraco/api/search/downloads", {
        method: "POST",
        body: JSON.stringify(formDataObject),
        headers: {
            "Content-Type": "application/json",
            "domain-id": document.querySelector("html")!.getAttribute("domain-id")
        }
    });
    const downloads = await res.json();
    data.downloads = [];
    setTimeout(() => {
        data.downloads = downloads.hits; 
    }, 0);
    data.pagination = downloads.pagination;
    data.currentPage = downloads.pagination.currentPage;
    updateUrl(formDataObject);
}

window.onGlobalSearchSubmit = async (ev: Event, data: any) => {
    const formDataObject: any = mapFormToObject(ev);
    if (ev.type == "input") {
        formDataObject.pageNumber = 0;
    }
    formDataObject.pageSize = data.pageSize;
    const res = await fetch("/umbraco/api/search/global", {
        method: "POST",
        body: JSON.stringify(formDataObject),
        headers: {
            "Content-Type": "application/json",
            "domain-id": document.querySelector("html")!.getAttribute("domain-id")
        }
    });
    const searchResult = await res.json();
    data.hits = searchResult.hits;
    data.pagination = searchResult.pagination;
    data.currentPage = searchResult.pagination.currentPage;
    data.facets = searchResult.facets;

    updateUrl(formDataObject);
}

const updateUrl = (formDataObject: any) => {
    const searchParams = new URLSearchParams(window.location.search)
    searchParams.set("query", formDataObject.searchQuery);
    searchParams.set("pageNumber", (Number.parseInt(formDataObject.pageNumber) + 1).toString());
    searchParams.set("pageSize", formDataObject.pageSize);
    const newRelativePathQuery = window.location.pathname + '?' + searchParams.toString();
    history.pushState(null, '', newRelativePathQuery);
}

window.onClearFilters = async (ev: Event, data: any) => {
    const form = ev.target!.closest("form") as HTMLFormElement;
    const filterInputs = form.querySelectorAll("[data-filter]");
    for (const filterInput of filterInputs) {
        filterInput.checked = false;
    }
    await window.onProjectListSearch(ev, data);
}

window.onClearFiltersSuggestion = async (ev, data) => {
    const form = ev.target.closest("form");

    if (!form) return;

    form.querySelectorAll('input[type="checkbox"]').forEach(input => {
        input.checked = false;
    });

    form.querySelectorAll('input[type="date"]').forEach(input => {
        input.value = "";
    });

    form.querySelectorAll('input[type="text"]').forEach(input => {
        input.value = "";
    });

    form.querySelectorAll('select').forEach(select => {
        select.selectedIndex = 0;
    });

    data.filters = {};
    data.filtersCount = 0;
    form.dispatchEvent(new Event("change"));

    await window.onSuggestionSearch(ev, data);
};


window.onProjectClick = async (id: number, data: any) => {
    if (data.project && data.project.id == id) {
        data.showModal = true;
    } else {
        const res = await fetch(`/umbraco/api/project/get?id=${id}`, {
            method: "GET",
            headers: {
                "domain-id": document.querySelector("html")!.getAttribute("domain-id")
            }
        });
        data.project = await res.json();
        data.showModal = true;
    }
}
window.onProcessTemplateClick = async (id: number, data: any) => {
    if (data.template && data.template.id == id) {
        data.showModal = true;
    } else {
        data.template = data.templates.filter((x: any) => x.id == id)[0];
        data.showModal = true;
    }
}

window.onProcessTemplateCloseClick = (data: any) => {
    data.showModal = false;
}

window.onProjectCloseClick = (data: any) => {
    data.showModal = false;
}

window.getProjectStageStatusPercentage = (status: number) => {
    const inProgess = "#e8bb02";
    const done = "#87af23";
    const current = status == 100 ? done : inProgess;
    return `background: linear-gradient(to right, ${current} ${status}%, #ffffff ${status}%);`;
}

window.onMilestoneTableSort = (key: string, data: any) => {
    if (data.lastSortedHeader == key && data.invert) {
        data.project.milestones = data.project.milestones.sort((a: any, b: any) => {
            return b[key].localeCompare(a[key]);
        });
    } else {
        data.project.milestones = data.project.milestones.sort((a: any, b: any) => {
            return a[key].localeCompare(b[key]);
        });
    }
    if (data.lastSortedHeader == key) {
        data.invert = !data.invert;
    } else {
        data.invert = true;
    }
    data.lastSortedHeader = key;
}

window.onPreviousPageClick = async (data: any, nextTick: any, refs: any) => {
    data.currentPage = data.currentPage - 1;
    await nextTick();
    refs.form.requestSubmit();
}
window.onNextPageClick = async (data: any, nextTick: any, refs: any) => {
    data.currentPage = data.currentPage + 1;
    await nextTick();
    refs.form.requestSubmit();
}

const getFiltersCount = (filters: any) => {
    if (!filters) {
        return 0;
    }
    const keys = Object.keys(filters);
    let filterCounter = 0;
    for (const key of keys) {
        filterCounter += filters[key].length;
    }
    return filterCounter;
}
const mapFormToObject = (ev: any) => {
    const form = ev.target.closest("form");
    const formData = new FormData(form);
    const formKeys = Object.keys(Object.fromEntries(formData.entries()));
    const formDataObject: any = {};
    for (const key of formKeys) {
        const value = formData.getAll(key);
        const subKey = key.split(".");
        if (subKey.length == 1) {
            formDataObject[key] = value[0];
        } else {
            if (subKey.length <= 1) {
                formDataObject[key] = formData.getAll(key);
            } else {
                if (formDataObject[subKey[0]] == undefined) {
                    formDataObject[subKey[0]] = {};
                }
                formDataObject[subKey[0]][subKey[1]] = formData.getAll(key);
            }
        }
    }
    return formDataObject;
}

window.translateFacetLabel = (label: string) => {
    if (!label) {
        console.log("No value");
        return
    }
    switch (label.toLowerCase()) {
        case "guideline":
            return "Riktlinje";
        case "previousGuideline":
            return "Tidigare riktlinje";
        case "nationalGuideline":
            return "Nationell riktlinje";
        case "environmentStandard":
            return "Miljökrav";
        case "environmentStandardTemplate":
            return "Miljökrav mall";
        case "technicalRequirement":
            return "Teknikrav";
        case "typeRoomWithGroup":
            return "Typrum";
        case "nationalTypeRoomWithGroup":
            return "Nationella Typrum";
        case "previousTypeRoomWithGroup":
            return "Tidigare Typrum";
        case "surface":
            return "Ytskikt";
        case "decoration":
            return "Inredning";
        case "functionFinish":
            return "Funktionsyta";
        case "functionRequirement":
            return "Funktionkrav";
        case "3dObject":
            return "3D objekt";
        case "cmspage":
            return "Webbsida";
        default:
            return label;
    }
};

window.setPageSize = (size: number, data: any) => {
    data.pageSize = size;
    data.currentPage = 0;
}
window.onCloseModal = (data: any) => {
    data.showModal = false;
}
window.onCalendarEventClick = async (id: number, data: any, nextTick: any) => {
    if (data.calendarEvent && data.calendarEvent.id == id && !data.calendarEvent.hideMap) {
        data.showModal = true;
        await nextTick();
        const map = new google.maps.Map(document.getElementById("map"), {
            zoom: 18,
            center: {lat: data.calendarEvent.centerCoordinatesLat, lng: data.calendarEvent.centerCoordinatesLng},
        });
        const marker = new google.maps.Marker({
            position: {lat: data.calendarEvent.centerCoordinatesLat, lng: data.calendarEvent.centerCoordinatesLng},
            map: map,
        });
    } else {
        const res = await fetch(`/umbraco/api/calendarevent/getbyid?id=${id}`, {
            method: "GET",
            headers: {
                "domain-id": document.querySelector("html")!.getAttribute("domain-id")
            }
        });
        data.calendarEvent = await res.json();
        data.showModal = true;
        await nextTick();
        if (!data.calendarEvent.hideMap) {
            const map = new google.maps.Map(document.getElementById("map"), {
                zoom: 18,
                center: {lat: data.calendarEvent.centerCoordinatesLat, lng: data.calendarEvent.centerCoordinatesLng},
            });
            const marker = new google.maps.Marker({
                position: {lat: data.calendarEvent.centerCoordinatesLat, lng: data.calendarEvent.centerCoordinatesLng},
                map: map,
            });
        }
    }
}

window.onSubmitCalendarRegistration = async (ev: SubmitEvent, data: any) => {
    data.errors = [];
    const formDataObject: any = mapFormToObject(ev);
    const res = await fetch("/umbraco/api/calendarevent/register", {
        method: "POST",
        body: JSON.stringify(formDataObject),
        headers: {
            "Content-Type": "application/json",
        }
    });
    if (res.ok) {
        console.log("OK", res);
    } else {
        data.errors = await res.json();
        console.log(data.errors);
    }
}

window.createReport = (el: any, data: any) => {
    const baseUrl = data.createReportBaseUrl;
    const inputs = el.parentNode.nextElementSibling.querySelectorAll("input:checked");
    if (inputs.length == 0) {
        return;
    }
    const values = [...inputs].map((x: any) => x.value);
    const ids = values.join(",");
    const url = `${baseUrl}?typrumIdnCsv=${ids}`;
    downloadFile(url);
}

window.massDownload = (el: any, data: any) => {
    const inputs = el.parentNode.nextElementSibling.querySelectorAll("input:checked");
    if (inputs.length == 0) {
        return;
    }
    const values = [...inputs].map((x: any) => x.value);
    const ids = values.join(",");
    const url = `/umbraco/api/Download/GetZippedDocument?revitfilId=${ids}`;
    downloadFile(url);
}

window.createSuggestion = (data: any) => {
    data.showCreateSuggestionModal = true;
}
window.onCreateSuggestionClose = (data: any) => {
    data.showCreateSuggestionModal = false;
}
window.onEditSuggestionClose = (data: any) => {
    data.showEditSuggestionModal = false;
}
window.onShowEditSuggestion = (data: any) => {
    data.showEditSuggestionModal = true;
    console.log(data.suggestion);
}
window.onShowSuggestion = (id: number, data: any) => {
    data.showSuggestionModal = true;
    data.suggestion = data.hits.filter((x: any) => x.id == id)[0];
}
window.onShowSuggestionClose = (data: any) => {
    data.showSuggestionModal = false;
}
window.toggleCreateComment = (data: any) => {
    data.showCreateComment = !data.showCreateComment;
}
window.onSuggestionVoteClick = async (id: any, data: any) => {
    const res = await fetch(`/umbraco/api/suggestion/vote?id=${id}`, {
        method: "POST"
    });
    if (res.ok) {
        const newSuggestion = await res.json();
        const hitsCopy = [...data.hits];
        const index = hitsCopy.findIndex((x: any) => x.id == newSuggestion.id);
        hitsCopy[index] = newSuggestion;
        data.hits = hitsCopy;
        if (data.suggestion && data.suggestion.id == newSuggestion.id) {
            data.suggestion = newSuggestion;
        }
    }
}
window.onCreateComment = async (ev: any, data: any) => {
    const formDataObject: any = mapFormToObject(ev);

    const res = await fetch(`/umbraco/api/suggestion/comment`, {
        method: "POST",
        body: JSON.stringify(formDataObject),
        headers: {
            "Content-Type": "application/json",
        }
    });
    if (res.ok) {
        const newSuggestion = await res.json();
        const hitsCopy = [...data.hits];
        const index = hitsCopy.findIndex((x: any) => x.id == newSuggestion.id);
        hitsCopy[index] = newSuggestion;
        data.hits = hitsCopy;
        data.suggestion = newSuggestion;
    }
}

window.onCreateSuggestionSubmit = async (ev: any, data: any) => {
    const form = ev.target.closest("form");
    const formData = new FormData(form);
    const fileInputs = form.querySelectorAll("[type='file']");
    for (const input of fileInputs) {
        for (const file of input.files) {
            formData.append("attachments", file);
        }
    }
    const res = await fetch(`/umbraco/api/suggestion/insert`, {
        method: "POST",
        body: formData,
    });
    if (res.ok) {
        data.hits = await res.json();
        data.formStatus = "success";
    }
}

window.onRemoveSuggestionFile = (id: number, data: any) => {
    data.filesToRemove.push(id);
}
window.onRestoreSuggestionFile = (id: number, data: any) => {
    data.filesToRemove = data.filesToRemove.filter((x: any) => x != id);
}

window.onEditSuggestionSubmit = async (ev: any, data: any) => {
    const form = ev.target.closest("form");
    const formData = new FormData(form);
    const fileInputs = form.querySelectorAll("[type='file']");
    for (const input of fileInputs) {
        for (const file of input.files) {
            formData.append("attachments", file);
        }
    }
    const res = await fetch(`/umbraco/api/suggestion/update`, {
        method: "PATCH",
        body: formData,
    });
    if (res.ok) {
        data.suggestion = await res.json();
        data.showEditSuggestionModal = false;
    }
}

window.onRemoveSuggestion = async (data: any) => {
    const res = await fetch(`/umbraco/api/suggestion/delete?id=${data.suggestion.id}`);
    if (res.ok) {
        data.showSuggestionRemoveModal = false;
        data.showSuggestionModal = false;
        data.suggestion = null;
        data.hits = await res.json();
    }
}

window.onRemoveSuggestionButton = async (data: any) => {
    data.showSuggestionRemoveModal = true;
}

window.onRemoveSuggestionModalClose = async (data: any) => {
    data.showSuggestionRemoveModal = false;
}

window.onShowIllustrationImageOpen = async (data: any, illustrationUrl: string) => {
    data.showIllustrationImageModal = true;
    data.illustrationUrl = illustrationUrl;
}
window.onShowIllustrationImageClose = async (data: any) => {
    data.showIllustrationImageModal = false;
}

window.onSortChange = (ev: any) => {
    const mode = ev.target.value;
    const ulNode = ev.target.parentNode.parentNode.querySelector("ul");
    const liNodes = [...ulNode.querySelectorAll("li")];
    const sortedLiNodes = liNodes.sort((a: any, b: any) => {
        let aValue;
        let bValue;
        if (mode == 1 || mode == 2) {
            aValue = a.querySelector(".name");
            bValue = b.querySelector(".name");
        } else if (mode == 3 || mode == 4) {
            aValue = a.querySelector(".region");
            bValue = b.querySelector(".region");
        }

        if (mode == 1 || mode == 3) {
            return aValue.textContent.localeCompare(bValue.textContent);
        } else if (mode == 2 || mode == 4) {
            return bValue.textContent.localeCompare(aValue.textContent);
        }
    });
    sortedLiNodes.forEach(x => {
        ulNode.appendChild(x);
    });
}

window.onSortChangeDownloads = async (ev: any, data: any) => {
    const form = ev.target.closest("form");
    const submitEvent = new SubmitEvent("submit", {submitter: ev.target});
    form.dispatchEvent(submitEvent);
}

window.onSuggestionSearch = async (ev, data) => {
    const formDataObject = mapFormToObject(ev);

    formDataObject.filters = formDataObject.filters || {};

    formDataObject.filters["tool"] = Array.from(document.querySelectorAll('input[name="tool"]:checked'))
        .map(el => el.value);
    formDataObject.filters["module"] = Array.from(document.querySelectorAll('input[name="module"]:checked'))
        .map(el => el.value);

    let fromDateInput = document.querySelector('input[x-model="fromDate"]')?.value || data.fromDate || null;
    let toDateInput = document.querySelector('input[x-model="toDate"]')?.value || data.toDate || null;

    let fromDate = fromDateInput ? new Date(fromDateInput).toISOString().split("T")[0] : null;
    let toDate = toDateInput ? new Date(toDateInput).toISOString().split("T")[0] : null;

    if (fromDate) formDataObject.filters["fromDate"] = [fromDate];
    if (toDate) formDataObject.filters["toDate"] = [toDate];

    formDataObject.searchQuery = formDataObject.searchQuery || "";
    formDataObject.pageNumber = data.currentPage || 0;
    formDataObject.pageSize = data.pageSize || 10;

    if (!formDataObject.filters["tool"].length &&
        !formDataObject.filters["module"].length &&
        !formDataObject.filters["fromDate"] &&
        !formDataObject.filters["toDate"]) {
        formDataObject.filters = {}; 
    }

    data.filtersCount = getFiltersCount(formDataObject.filters);

    const res = await fetch("/umbraco/api/search/suggestions", {
        method: "POST",
        body: JSON.stringify(formDataObject),
        headers: {
            "Content-Type": "application/json",
            "domain-id": document.querySelector("html")?.getAttribute("domain-id")
        }
    });

    if (!res.ok) {
        console.error("Search request failed:", res.statusText);
        return;
    }

    const search = await res.json();
    if (!search || !search.hits || !search.pagination) {
        console.error("Invalid search response data");
        return;
    }

    data.hits = search.hits;
    data.facets = search.facets;
    data.pagination = search.pagination;
    data.currentPage = search.pagination.CurrentPage;
    data.toolTypes = search.toolTypes;
    data.moduleTypes = search.moduleTypes;
};

// Source: http://pixelscommander.com/en/javascript/javascript-file-download-ignore-content-type/
window.downloadFile = function (sUrl) {

    //iOS devices do not support downloading. We have to inform user about this.
    if (/(iP)/g.test(navigator.userAgent)) {
        //alert('Your device does not support files downloading. Please try again in desktop browser.');
        window.open(sUrl, '_blank');
        return false;
    }

    //If in Chrome or Safari - download via virtual link click
    if (window.downloadFile.isChrome || window.downloadFile.isSafari) {
        //Creating new link node.
        var link = document.createElement('a');
        link.href = sUrl;
        link.setAttribute('target', '_blank');

        if (link.download !== undefined) {
            //Set HTML5 download attribute. This will prevent file from opening if supported.
            var fileName = sUrl.substring(sUrl.lastIndexOf('/') + 1, sUrl.length);
            link.download = fileName;
        }

        //Dispatching click event.
        if (document.createEvent) {
            var e = document.createEvent('MouseEvents');
            e.initEvent('click', true, true);
            link.dispatchEvent(e);
            return true;
        }
    }

    // Force file download (whether supported by server).
    if (sUrl.indexOf('?') === -1) {
        sUrl += '?download';
    }

    window.open(sUrl, '_blank');
    return true;
};

window.downloadFile.isChrome = navigator.userAgent.toLowerCase().indexOf('chrome') > -1;
window.downloadFile.isSafari = navigator.userAgent.toLowerCase().indexOf('safari') > -1;

window.toggleShowVotesTable = (data: any) => {
    data.showVotesTable = !data.showVotesTable;
}

window.downloadGroup = ($event: any) => {
    console.log($event);
    const target = $event.target;
    const links = target.closest("li").querySelectorAll("a");
    links.forEach((x: any) => {
            const link = x.getAttribute("href");
            downloadFile(link);
    });
}
function initCookieConsent() {

    // Get "cookiesText" and "cookiesPage" values from _Layout.cshtml
    const cookiesText = (window as any).cookiesText;
    const cookiesPage = (window as any).cookiesPage;

    window.cookieconsent.initialise({
        palette: {
            popup: { background: "#859c44", text: "#FFF" },
            button: { background: "transparent", text: "#FFF", border: "#FFF" }
        },
        showLink: true,
        position: "top",
        static: true,
        content: {
            message: cookiesText,
            dismiss: "Jag förstår",
            link: "Läs mer om cookies",
            href: cookiesPage
        }
    });
}

Alpine.data('slider', () => ({
    currentSlide: 0,
    images: [],

    init(images: { url: string; thumbUrl: string; alt: string; meta: string }[]) {
        this.images = images;
    },

    prevSlide() {
        this.currentSlide = this.currentSlide > 0 ? this.currentSlide - 1 : this.images.length - 1;
    },

    nextSlide() {
        this.currentSlide = (this.currentSlide + 1) % this.images.length;
    },

    goToSlide(index: number) {
        this.currentSlide = index;
    }
}));


Alpine.start();

document.addEventListener('DOMContentLoaded', function () {
    document.querySelectorAll("iframe.aspect-video").forEach(video => {
        const src = video.getAttribute("src");
        video.setAttribute("src", src + "&rel=0");
    });
    initCookieConsent();
});